import { useEffect, useMemo, useState } from 'react';
import { Building2, Check, Loader2, Search } from 'lucide-react';
import type { Employer } from '@pm-next/shared-types';
import { useAuth } from '@/auth/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

interface CompanySwitcherProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function matchesSearch(employer: Employer, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    employer.name.toLowerCase().includes(q) ||
    employer.code.toLowerCase().includes(q) ||
    (employer.shortname?.toLowerCase().includes(q) ?? false)
  );
}

export function CompanySwitcher({ open, onOpenChange }: CompanySwitcherProps) {
  const { selectedEmployer, fetchMyEmployers, switchEmployer } = useAuth();
  const { toast } = useToast();
  const [employers, setEmployers] = useState<Employer[]>([]);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;

    setSearch('');
    setSelectedId(selectedEmployer?.id ?? null);
    setIsLoading(true);

    fetchMyEmployers()
      .then((list) => setEmployers(list))
      .catch((err) => {
        toast({
          variant: 'destructive',
          title: 'Could not load companies',
          description: err instanceof Error ? err.message : 'Please try again.',
        });
      })
      .finally(() => setIsLoading(false));
  }, [open, selectedEmployer?.id, fetchMyEmployers, toast]);

  const filtered = useMemo(
    () => employers.filter((employer) => matchesSearch(employer, search)),
    [employers, search]
  );

  const handleSwitch = async () => {
    if (selectedId === null || selectedId === selectedEmployer?.id) {
      onOpenChange(false);
      return;
    }

    setIsSubmitting(true);
    try {
      await switchEmployer(selectedId);
      toast({
        title: 'Company switched',
        description: 'Your workspace is now using the selected company.',
      });
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Switch failed',
        description: err instanceof Error ? err.message : 'Could not switch company.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Switch company</DialogTitle>
          <DialogDescription>
            Search and select another company you have access to.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or code…"
            className="pl-9"
            disabled={isLoading || isSubmitting}
            autoFocus
            aria-label="Search companies"
          />
        </div>

        <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading companies…
            </div>
          ) : filtered.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {employers.length === 0
                ? 'No companies available.'
                : `No companies match “${search.trim()}”.`}
            </p>
          ) : (
            filtered.map((employer) => {
              const isSelected = selectedId === employer.id;
              const isCurrent = selectedEmployer?.id === employer.id;
              return (
                <button
                  key={employer.id}
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => setSelectedId(employer.id)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors',
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-transparent hover:bg-accent'
                  )}
                >
                  <div
                    className={cn(
                      'flex h-9 w-9 shrink-0 items-center justify-center rounded-md',
                      isSelected ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                    )}
                  >
                    <Building2 className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium">{employer.name}</span>
                      {isCurrent && (
                        <span className="shrink-0 text-xs text-muted-foreground">Current</span>
                      )}
                    </div>
                    <p className="truncate text-xs text-muted-foreground">{employer.code}</p>
                  </div>
                  {isSelected && <Check className="h-4 w-4 shrink-0 text-primary" />}
                </button>
              );
            })
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSwitch}
            disabled={isLoading || isSubmitting || selectedId === null}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Switching…
              </>
            ) : (
              'Switch company'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
